import moment from "moment";
import {numberWithCommasInputChange, numberWithCommasToBack} from "../../util/util";
import LocalStorage from "../../util/localStorage";

function isString(obj) {
    return (Object.prototype.toString.call(obj) === '[object String]');
}

const year = moment().format("YYYY")

export class Field {
    constructor(name, value = '', validate = ['empty'], disabled = false, type, metadata, props = {}) {
        this.name = name;
        this.value = !!value ? value : "";
        this.errorMessage = null;
        this.validate = validate;
        this.isEmpty = !this.value || this.value.length === 0;
        this.disabled = disabled;
        this.type = type;
        this.metadata = metadata;
        this.props = props;
    }
}

export class FieldsManager {
    static updateField(fields, name, newValue) {
        let clone = Object.assign({}, fields);

        if (!!clone[name].errorMessage) {
            clone[name].errorMessage = null;
        }

        clone[name].value = FieldsManager.formatValue(newValue, clone[name].validate);
        return clone;
    }

    static updateFieldType(fields, name, newType) {
        let clone = Object.assign({}, fields);
        clone[name].type = newType;
        return clone;
    }

    static formatValue(value, validate) {
        if (
            (validate.indexOf('float') > -1)
            || (validate.indexOf('float_or_empty') > -1 || (validate.indexOf('float_not_require') > -1))
        ) {
            return numberWithCommasInputChange(value);
        }
        if (
            (validate.indexOf('integer') > -1)
            || (validate.indexOf('integer_or_empty') > -1) || (validate.indexOf('integer_not_require') > -1)
        ) {
            return value.replace(/\D+/g, '');
        }

        return value;
    }

    static updateAndValidateField(fields, name, newValue) {
        let clone = Object.assign({}, fields);
        clone[name].value = FieldsManager.formatValue(newValue, clone[name].validate);
        clone[name] = FieldsManager.validateField(clone[name]);
        return clone;
    }

    static validateFields(fields, list = []) {
        return Object.keys(fields)
            .reduce((memo, key) => {
                if (list.length === 0 || !!~list.indexOf(key)) {
                    memo[key] = FieldsManager.validateField(fields[key]);
                } else {
                    memo[key] = fields[key];
                }
                return memo;
            }, {});
    }

    static cleanFields(fields) {
        return Object.keys(fields).reduce((memo, key) => {
            memo[key] = Object.assign({}, fields[key], {value: ""});
            return memo;
        }, {});
    }

    static validateField(field) {
        const errorMessage = field.validate.reduce((memo, type) => {
            switch (type) {
                case 'empty_select_search':
                    if (!field.value || (isString(field.value) && field.value.trim() === "") || !field.value.value) {
                        memo.push('fields.errors.empty');
                    }
                    break;
                case 'empty':
                case 'custom':
                    if (!field.value || (isString(field.value) && field.value.trim() === "")) {
                        memo.push('fields.errors.empty');
                    }
                    break;
                case 'empty_array':
                    if (!field.value || !field.value.length) {
                        memo.push('fields.errors.empty');
                    }
                    break;
                case 'email':
                    if (!/^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/gi.test(field.value)) {
                        memo.push('fields.errors.email');
                    }
                    break;
                case 'password':
                    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#\$%\^&\*]{8,}$/g.test(field.value)) {
                        memo.push('fields.errors.password');
                    }
                    break;
                case 'pin_four_digits':
                    if (!/^\d{4}$/g.test(field.value)) {
                        memo.push('fields.errors.pin_four_digits');
                    }
                    break;
                case 'date':
                    if (!field.value) {
                        // Just pass, empty is fine
                    } else if (!FieldsManager.checkDateFormat(field.value)) {
                        memo.push('fields.error.date_format');
                    }
                    break;
                case 'future_date':
                    if (!field.value) {
                        // Just pass, empty is fine
                    } else if (!FieldsManager.checkDateFormat(field.value)) {
                        memo.push('fields.error.date_format');
                    } else if (!FieldsManager.checkDateDigits(field.value)) {
                        memo.push('fields.error.date_format_digits');
                    } else if (!FieldsManager.isDateInTheFuture(field.value)) {
                        memo.push('fields.error.outdated_date');
                    }
                    break;
                case 'integer_or_empty':
                    if ((field.value !== "") && !/^[0-9]+$/g.test(field.value)) {
                        memo.push('fields.errors.integer');
                    }
                    break;
                case 'integer':
                    if (!/^[0-9]+$/g.test(field.value)) {
                        memo.push('fields.errors.integer');
                    }
                    break;
                case 'integer_up_to_100':
                    if ((field.value !== "") && (!/^[0-9]+$/g.test(field.value)) || (field.value > 100)) {
                        memo.push('fields.errors.integer_up_to_100');
                    }
                    break;
                case 'integer_up_to_256':
                    if ((field.value !== "") && (!/^[0-9]+$/g.test(field.value)) || (field.value > 256)) {
                        memo.push('fields.errors.integer_up_to_256');
                    }
                    break;
                case 'string_up_to_100':
                    if (field.value.length > 100) {
                        memo.push('fields.errors.string_up_to_100');
                    }
                    break;
                case 'string_up_to_256':
                    if (field.value.length > 256) {
                        memo.push('fields.errors.string_up_to_256');
                    }
                    break;
                case 'float_or_empty':
                    if (!(field.value !== "" || !field.value) && !/^[0-9,.]*$/g.test(field.value)) {
                        memo.push('fields.errors.float');
                    }
                    break;
                case 'float':
                    if (!/\d+(\.\d+)?/g.test(field.value)) {
                        memo.push('fields.errors.float');
                    }
                    break;
                case 'float_up_to_100':
                    if ((field.value !== "") && !/\d+(\.\d+)?/g.test(field.value)) {
                        memo.push('fields.errors.float_up_to_100');
                    } else if ((field.value !== "") && parseFloat(field.value) > 100) {
                        memo.push('fields.errors.float_up_to_100');
                    }
                    break;
                case 'year':
                    if (field.value < 0 || field.value > (year)) {
                        memo.push('fields.errors.wrong_year');
                    }
                    break;
                case 'bigger_than_0':
                    if (field.value < 0) {
                        memo.push('fields.errors.number_less_than_0');
                    }
                    break;
                case 'integer_not_require':
                    if (!/^[0-9]*$/g.test(field.value)) {
                        memo.push('fields.errors.integer');
                    }
                    break;
                case 'float_not_require':
                    if (!/^((\d+\.?|\.(?=\d))?\d{0,9})$/g.test(field.value)) {
                        memo.push('fields.errors.float');
                    }
                    break;
                default:
                    break;
            }
            return memo;
        }, []);
        field.isEmpty = !field || !field.value || field.value.length === 0;

        return Object.assign({}, field, {
            errorMessage: errorMessage.length ? errorMessage : null
        });
    }

    static checkFieldsForErrors(fields, list = []) {
        return Object.keys(fields)
            .reduce((memo, key) => {
                if (list.length === 0 || !!~list.indexOf(key)) {
                    return memo && (!fields[key].errorMessage);
                } else {
                    return memo;
                }
            }, true);
    }

    static disableFields(fields, list = []) {
        return Object.keys(fields)
            .reduce((memo, key) => {
                if (list.length === 0 || !!~list.indexOf(key)) {
                    fields[key].disabled = true;
                    memo[key] = fields[key];
                } else {
                    memo[key] = fields[key];
                }
                return memo;
            }, {});
    }

    static enableFields(fields, list = []) {
        return Object.keys(fields)
            .reduce((memo, key) => {
                if (list.length === 0 || !!~list.indexOf(key)) {
                    fields[key].disabled = false;
                    memo[key] = fields[key];
                } else {
                    memo[key] = fields[key];
                }
                return memo;
            }, {});
    }

    static checkDateFormat(dateString) {
        // First check for the pattern
        return /^\d{1,2} \/ \d{4}$/.test(dateString);
    }

    static checkDateDigits(dateString) {
        // Parse the date parts to integers
        const parts = dateString.split('/');
        const month = parseInt(parts[0].trim(), 10);
        const year = parseInt((parts[1].trim()), 10);

        // Check the ranges of month and year
        return !(year < 1000 || year > 3000 || month === 0 || month > 12);
    }

    static isDateInTheFuture(dateString) {
        const expDate = moment(dateString, 'MM / YYYY').toDate();
        const now = moment(moment().format('MM / YYYY'), 'MM / YYYY').toDate();
        return !((expDate - now) < 0);
    }

    static getFieldKeyValues(fields) {
        return Object.keys(fields)
            .reduce((memo, key) => {
                switch (fields[key].type) {
                    case 'select-search':
                        if (!!fields[key]?.props?.isMulti) {
                            memo[key] = fields[key].value.map(it => it.value);
                        } else {
                            memo[key] = fields[key].value ? fields[key].value.value : "";
                        }
                        break;
                    case 'checkbox':
                        memo[key] = !!fields[key].value ? 1 : 0;
                        break;
                    case 'multi-select':
                        memo[key] = fields[key].value?.map((it) => it.value) ?? [];
                        break;
                    case 'datetime': {
                        let date = fields[key].value

                        memo[key] = moment(date).format("YYYY-MM-DD HH:mm:ss")
                        break;
                    }
                    case 'datetimezone': {
                        let date = fields[key].value
                        let timezone = LocalStorage.get("user")?.Contact?.Timezone
                        memo[key] = moment.tz(date, timezone).utc().format("YYYY-MM-DD HH:mm:ss")
                        break;
                    }
                    default:
                        memo[key] = fields[key].value;
                        break;
                }

                switch (fields[key].validate[0]) {
                    case 'float':
                    case 'float_or_empty':
                        memo[key] = numberWithCommasToBack(fields[key].value);
                        break;
                    default:
                        break;
                }

                return memo;
            }, {});
    }
}
